import DocsLayout, { Callout } from '../../components/docs/DocsLayout';

export default function DocsArchitecture() {
  return (
    <DocsLayout 
      title="Architecture" 
      description="Understanding how AIBL works under the hood"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          AIBL is built on a layered architecture that separates concerns between permission management, execution, and settlement. This design ensures security while enabling autonomous agent operations.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">System Overview</h2>
        
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <pre className="text-sm text-white/70 font-mono whitespace-pre-wrap">
{`┌─────────────────────────────────────────────────────┐
│                  Developer Dashboard                 │
│         (Configuration & Monitoring Layer)           │
└─────────────────────────────────────────────────────┘
                          │
                          ▼
┌─────────────────────────────────────────────────────┐
│                    AIBL API Layer                    │
│           (Permission & Validation Engine)           │
└─────────────────────────────────────────────────────┘
                          │
                          ▼
┌─────────────────────────────────────────────────────┐
│                  Execution Layer                     │
│    ┌───────────┐  ┌───────────┐  ┌───────────┐     │
│    │  Turnkey  │  │  Gnosis   │  │  Bundler  │     │
│    │    MPC    │  │   Safe    │  │  Network  │     │
│    └───────────┘  └───────────┘  └───────────┘     │
└─────────────────────────────────────────────────────┘
                          │
                          ▼
┌─────────────────────────────────────────────────────┐
│              Blockchain Settlement Layer             │
│   (Ethereum, Base, Arbitrum, Polygon, Optimism...)   │
└─────────────────────────────────────────────────────┘`}
          </pre>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Core Components</h2>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">ERC-4337 Smart Accounts</h3>
        <p className="text-white/70">
          Each agent wallet is an ERC-4337 compliant smart account. This enables gasless transactions, batched operations, and programmable access control. The smart account acts as the on-chain representation of your agent.
        </p>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Turnkey MPC Signing</h3>
        <p className="text-white/70">
          Private keys are secured using Turnkey's MPC (Multi-Party Computation) infrastructure. Keys never exist in a single location, eliminating single points of failure while enabling fast transaction signing.
        </p>

        <Callout type="success" title="Security Note">
          Your agent's private keys are never exposed to AIBL servers. All signing operations happen within Turnkey's secure enclaves.
        </Callout>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Gnosis Safe Integration</h3>
        <p className="text-white/70">
          For high-value operations, agents can be upgraded to Gnosis Safe multi-signature wallets. This adds human-in-the-loop approval for operations above certain thresholds.
        </p>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Plugin Engine</h3>
        <p className="text-white/70">
          The plugin system validates all agent operations against configured permissions before execution. Each plugin defines a set of allowed actions and parameters that agents must operate within.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Request Flow</h2>

        <ol className="space-y-4 text-white/70">
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">1</span>
            <div>
              <strong className="text-white">API Request</strong>
              <p>Your application sends a transaction request to the AIBL API with the agent's API key.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">2</span>
            <div>
              <strong className="text-white">Permission Check</strong>
              <p>The plugin engine validates the request against the agent's configured permissions and spending limits.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">3</span>
            <div>
              <strong className="text-white">Transaction Building</strong>
              <p>If approved, the system builds the transaction calldata for the target protocol.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">4</span>
            <div>
              <strong className="text-white">Signing</strong>
              <p>Turnkey MPC signs the UserOperation without exposing the private key.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">5</span>
            <div>
              <strong className="text-white">Bundling & Submission</strong>
              <p>The signed operation is sent to bundlers who include it in the next block.</p>
            </div>
          </li>
        </ol>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Supported Chains</h2>
        
        <p className="text-white/70">
          AIBL currently supports 20+ EVM-compatible chains including:
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {['Ethereum', 'Base', 'Arbitrum', 'Polygon', 'Optimism', 'Avalanche', 'BSC', 'Fantom'].map(chain => (
            <div key={chain} className="p-3 rounded-lg bg-white/5 border border-white/10 text-center text-sm text-white/70">
              {chain}
            </div>
          ))}
        </div>
      </section>
    </DocsLayout>
  );
}
